/**
 * Bounds render-time errors so one crashed component doesn't blank the
 * whole OEL/CML pane. Shows a minimal "something went wrong" card with
 * a reload link instead of an empty page. Real bug still logs to the
 * browser console via componentDidCatch so it's debuggable.
 *
 * Added per OEL/CML audit finding #2 (2026-04-15): we had zero React
 * error boundaries, and CML's company-select bug earlier that week
 * produced exactly this failure mode — null deref in one render path
 * blanked the entire pane because nothing caught it.
 */
import { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Optional override — e.g. "CML pane" vs "OEL pane" for the message. */
  paneName?: string;
}

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught:', error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const paneName = this.props.paneName || 'pane';
    return (
      <div
        style={{
          padding: '1.5rem',
          textAlign: 'center',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
        }}
      >
        <div style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
          Something went wrong in the {paneName}.
        </div>
        <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '1rem', wordBreak: 'break-word' }}>
          {error.message || 'Unknown error'}
        </div>
        <button
          onClick={this.handleReset}
          style={{
            padding: '0.5rem 1.25rem',
            border: '1px solid #0078d4',
            background: '#0078d4',
            color: 'white',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.875rem',
          }}
        >
          Try again
        </button>
        <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '1rem' }}>
          If this keeps happening, reload the add-in (close + reopen this email) and check the browser console for details.
        </div>
      </div>
    );
  }
}
